import Utils from "@/core/utils/Utils";

/**
 * Кастомная функция валидации (ошибка, предупреждение, успех).
 * Возвращает текст, null (сообщения нет) или Promise с тем же — тогда асинхронная.
 */
type ValidationFn = (value: unknown) => string | Promise<string | null> | null;

type ValidationProps = {
  required?:       boolean | null;
  min?:            number  | null;
  max?:            number  | null;
  minLength?:      number  | null;
  maxLength?:      number  | null;
  email?:          boolean | null;
  name?:           boolean | null;
  phone?:          boolean | null;
  validateMethod?: ValidationFn | ValidationFn[] | null;
  warningMethod?:  ValidationFn | ValidationFn[] | null;
  successMethod?:  ValidationFn | null;
};

/**
 * Validation — модель состояния и правил валидации одного контрола.
 *
 * Контрол получает экземпляр через статический Validation.fromProps(this) —
 * фабрика читает стандартные пропы контрола и собирает по ним правила.
 *
 * Пропы, которых на конкретном контроле нет, просто пропускаются.
 *
 * Если нужно нестандартное правило — контрол переопределяет метод
 * buildValidation() и собирает Validation вручную через fluent-API.
 */
export default class Validation {
  // ==================== Состояние ====================

  isLoading: boolean       = false;
  isValid:   boolean       = true;
  isTouched: boolean       = false;
  isDirty:   boolean       = false;
  errors:    string[]      = [];
  warnings:  string[]      = [];
  success:   string | null = null;

  // ==================== Правила ====================

  /**
   * Контрол выключен. Не правило, а условие применимости всех правил:
   * синхронизируется из пропа disabled, поэтому меняется по ходу жизни
   * контрола, в отличие от правил, собранных фабрикой один раз.
   */
  private _isDisabled:     boolean = false;

  private _required:       boolean = false;
  private _min:             number | null = null;
  private _max:             number | null = null;
  private _minLength:       number | null = null;
  private _maxLength:       number | null = null;
  private _email:           boolean = false;
  private _name:            boolean = false;
  private _phone:           boolean = false;
  private _validateMethods: ValidationFn[] = [];
  private _warningMethods:  ValidationFn[] = [];
  private _successMethod:   ValidationFn | null = null;

  // ==================== Статическая фабрика ====================

  /**
   * Собирает Validation из стандартных пропов контрола.
   * Все пропы опциональны — пропуск означает «правило не применяется».
   */
  static fromProps(host: ValidationProps): Validation {
    const validation = new Validation();

    if (host.required === true) validation.required();

    if (host.min !== null && host.min !== undefined) validation.min(host.min);
    if (host.max !== null && host.max !== undefined) validation.max(host.max);

    if (host.minLength !== null && host.minLength !== undefined) validation.minLength(host.minLength);
    if (host.maxLength !== null && host.maxLength !== undefined) validation.maxLength(host.maxLength);

    if (host.email === true) validation.email();
    if (host.name  === true) validation.name();
    if (host.phone === true) validation.phone();

    /** Кастомные функции регистрируем обёртками, которые берут функцию из
     *  пропа в момент вызова, а не при сборке: шаблон передаёт новую стрелку
     *  на каждом рендере, а фабрика вызывается один раз. */
    if (Array.isArray(host.validateMethod)) {
      host.validateMethod.forEach((_fn, index) => {
        validation.validateMethod(value => (host.validateMethod as ValidationFn[])[index](value));
      });
    }
    else if (typeof host.validateMethod === 'function') {
      validation.validateMethod(value => (host.validateMethod as ValidationFn)(value));
    }

    if (Array.isArray(host.warningMethod)) {
      host.warningMethod.forEach((_fn, index) => {
        validation.warningMethod(value => (host.warningMethod as ValidationFn[])[index](value));
      });
    }
    else if (typeof host.warningMethod === 'function') {
      validation.warningMethod(value => (host.warningMethod as ValidationFn)(value));
    }

    if (typeof host.successMethod === 'function') {
      validation.successMethod(value => (host.successMethod as ValidationFn)(value));
    }

    return validation;
  }

  // ==================== Fluent-API конфигурации правил ====================

  /**
   * Выключенный контрол не валидируется. Заполнить его пользователь не может,
   * поэтому требовать с него нечего, а невалидная форма из-за недоступного
   * поля — тупик: ошибку видно, исправить нельзя.
   */
  setDisabled(isDisabled: boolean): this {
    this._isDisabled = isDisabled;
    return this;
  }

  /** Значение не должно быть пустым. */
  required(): this {
    this._required = true;
    return this;
  }

  /** Значение должно быть >= min. */
  min(min: number): this {
    this._min = min;
    return this;
  }

  /** Значение должно быть <= max. */
  max(max: number): this {
    this._max = max;
    return this;
  }

  /** Сахар: min + max одним вызовом. */
  between(min: number, max: number): this {
    this._min = min;
    this._max = max;
    return this;
  }

  /** Длина строки должна быть >= minLength. */
  minLength(minLength: number): this {
    this._minLength = minLength;
    return this;
  }

  /** Длина строки должна быть <= maxLength. */
  maxLength(maxLength: number): this {
    this._maxLength = maxLength;
    return this;
  }

  /** Значение должно быть корректным email. */
  email(): this {
    this._email = true;
    return this;
  }

  /** Значение должно быть корректным именем (только буквы, пробелы, дефисы). */
  name(): this {
    this._name = true;
    return this;
  }

  /** Значение должно быть корректным телефоном. */
  phone(): this {
    this._phone = true;
    return this;
  }

  /**
   * Кастомный валидатор. Возвращает строку (текст ошибки) или null (всё ок).
   * Может вернуть Promise — тогда правило асинхронное.
   * Можно цепочкой добавить несколько раз — все будут вызваны.
   */
  validateMethod(fn: ValidationFn): this {
    this._validateMethods.push(fn);
    return this;
  }

  /**
   * Предупреждение. В отличие от validateMethod, результат НЕ влияет на isValid.
   * Может вернуть Promise — как и validateMethod.
   */
  warningMethod(fn: ValidationFn): this {
    this._warningMethods.push(fn);
    return this;
  }

  /** Сообщение об успехе. Может вернуть Promise — как и validateMethod. */
  successMethod(fn: ValidationFn): this {
    this._successMethod = fn;
    return this;
  }

  // ==================== Управление состоянием ====================

  markTouched(): void {
    this.isTouched = true;
  }

  markDirty(): void {
    this.isDirty = true;
  }

  /** Сбросить состояние к начальному. Правила сохраняются. */
  reset(): void {
    this.isLoading = false;
    this.isValid   = true;
    this.isTouched = false;
    this.isDirty   = false;
    this.errors    = [];
    this.warnings  = [];
    this.success   = null;
  }

  // ==================== Запуск валидации ====================

  /**
   * Прогоняет все правила по значению, обновляет state.
   * Возвращает Promise, который резолвится после завершения ВСЕХ проверок —
   * включая асинхронные. Контрол должен await'ить вызов.
   */
  async validate(value: unknown): Promise<void> {
    /** Выключенный контрол правилам не подчиняется. Состояние сбрасываем:
     *  ошибки, оставшиеся с того момента, когда контрол был включён, не
     *  должны блокировать форму. */
    if (this._isDisabled === true) {
      this.reset();
      return;
    }

    // Локальные накопители — собираем сюда всё, что найдём по правилам,
    // и в конце разом присвоим в this.errors / this.warnings. Так Vue
    // увидит изменение одной операцией, а не серией push'ей.
    const errors:   string[] = [];
    const warnings: string[] = [];

    // ===== required =====

    // Проверяем required только если оно сконфигурировано на классе.
    // Сама проверка — в Utils.validators.required (null/undefined/пустая строка/массив).
    if (this._required && Utils.validators.required(value) === false) {
      errors.push(this.getStandardMessage('required'));
    }

    // ===== min / max =====

    // min: значение должно быть >= указанного минимума. Если min не задан — пропускаем.
    if (this._min !== null && Utils.validators.min(value as number | null, this._min) === false) {
      errors.push(this.getStandardMessage('min', this._min));
    }
    // max: симметрично для верхней границы.
    if (this._max !== null && Utils.validators.max(value as number | null, this._max) === false) {
      errors.push(this.getStandardMessage('max', this._max));
    }

    // ===== minLength / maxLength =====

    // Проверка длины строки/массива. Utils.validators.minLength сам кастит число в строку при нужде.
    if (this._minLength !== null && Utils.validators.minLength(value, this._minLength) === false) {
      errors.push(this.getStandardMessage('minLength', this._minLength));
    }
    if (this._maxLength !== null && Utils.validators.maxLength(value, this._maxLength) === false) {
      errors.push(this.getStandardMessage('maxLength', this._maxLength));
    }

    // ===== email / name / phone =====

    // Все три — булевы правила: либо включены, либо нет. Логика регэкспов в ValidatorUtils.
    if (this._email && Utils.validators.email(value as string) === false) {
      errors.push(this.getStandardMessage('email'));
    }
    if (this._name && Utils.validators.name(value as string) === false) {
      errors.push(this.getStandardMessage('name'));
    }
    if (this._phone && Utils.validators.phone(value as string) === false) {
      errors.push(this.getStandardMessage('phone'));
    }

    // ===== validate methods (кастомные валидаторы) =====

    // Сюда складываем Promise'ы от async-валидаторов, чтобы потом ждать их разом через Promise.all.
    const asyncResults: Array<Promise<string | null>> = [];

    // Прогоняем все зарегистрированные кастомные валидаторы.
    for (const fn of this._validateMethods) {
      const result = fn(value);

      // null — валидатор сказал "всё ок", ничего не пушим.
      if (result === null) {
        continue;
      }
      // Строка — синхронная ошибка, добавляем в общий список errors.
      else if (typeof result === 'string') {
        errors.push(result);
      }
      // Promise — асинхронный валидатор, откладываем на потом.
      else if (result instanceof Promise) {
        asyncResults.push(result);
      }
    }

    // ===== warning methods (предупреждения) =====

    // Симметрично validate: синхронный результат сразу, Promise — откладываем.
    const asyncWarnings: Array<Promise<string | null>> = [];

    for (const fn of this._warningMethods) {
      const result = fn(value);

      if (result === null) {
        continue;
      }
      else if (typeof result === 'string') {
        warnings.push(result);
      }
      else if (result instanceof Promise) {
        asyncWarnings.push(result);
      }
    }

    // ===== success =====

    // Сообщение об успехе. Строка попадёт в success сразу, Promise — после резолва.
    let success:      string | null                 = null;
    let asyncSuccess: Promise<string | null> | null = null;

    if (this._successMethod !== null) {
      const result = this._successMethod(value);

      if (result instanceof Promise) {
        asyncSuccess = result;
      }
      else {
        success = result;
      }
    }

    // ===== Применяем синхронную часть =====

    // Разом подменяем массивы — Vue увидит реактивное изменение этих полей.
    this.errors   = errors;
    this.warnings = warnings;
    this.success  = success;
    // isValid зависит ТОЛЬКО от errors. Warnings на validity не влияют — это их семантика.
    this.isValid  = errors.length === 0;

    // ===== Асинхронная часть =====

    // Если async-функций не было — на этом всё, состояние финализировано.
    if (asyncResults.length === 0 && asyncWarnings.length === 0 && asyncSuccess === null) {
      this.isLoading = false;
      return;
    }

    // Есть async-функции — поднимаем флаг isLoading на время их выполнения.
    this.isLoading = true;

    // Ждём резолва ВСЕХ async-функций разом и дописываем результаты в состояние.
    const [errorResults, warningResults] = await Promise.all([
      Promise.all(asyncResults),
      Promise.all(asyncWarnings),
    ]);

    for (const result of errorResults) {
      if (typeof result === 'string') {
        this.errors.push(result);
      }
    }

    for (const result of warningResults) {
      if (typeof result === 'string') {
        this.warnings.push(result);
      }
    }

    if (asyncSuccess !== null) {
      this.success = await asyncSuccess;
    }
    // Пересчитываем validity после async-добавок.
    this.isValid   = this.errors.length === 0;
    // Async-валидация закончилась — снимаем флаг isLoading.
    this.isLoading = false;
  }

  // ==================== Стандартные сообщения ====================

  /**
   * Возвращает стандартное сообщение об ошибке по id правила.
   * Для правил с числовым параметром (min/max/minLength/maxLength) добавляет число к тексту.
   */
  private getStandardMessage(errorId: string, value?: number): string {
    const base = Utils.validators.MESSAGES[errorId];

    switch (errorId) {
      case 'min':
      case 'max':
      case 'minLength':
      case 'maxLength':
        return `${base} ${value}`;
      case 'email':
      case 'name':
      case 'phone':
      case 'required':
      default:
        return base;
    }
  }
}